/* eslint-disable quotes */
import { api } from './index';
import {getApplication, onSubmitAgent, putApplication, removeTempFields} from './applicationAPI';
import { PRODUCTION } from '../components/PortalApp';
import { navigateWithData } from '../navigation/navigationHistory';
import { traverseObject } from '../utils/objectUtils';
import { GA_CONFIGURATION, sendGoogleAnalyticsEvent } from "../utils/googleAnalyticsUtils";

const handleError = async (error, systemId, applicationNumber, where) => {
  if (!PRODUCTION) console.log('Failure in ' + where + '(): ' + error.message);
  if ((error.response && (error.response.status === 503)) || (error.message == 'Network Error')) {
    navigateWithData('/server-maintenance', applicationNumber);
  } else {
    await onSubmitAgent(systemId, applicationNumber);
  }
}

const TO_DATA = r => (r ? r.data : null);

export const updatePaymentPlansAndBuy = async (
  application,
  request,
  onSuccess,
  onFailure
) => {
  try {
    const {downPaymentAmount, ...updateRequest} = request;

    if (application.typeCd !== 'Application') {
      await postBindRequest(application.systemId);
    }

    await putApplicationPaymentPlans(
        application.systemId,
        transformPaymentPlanRequest(updateRequest)
    );

    if (downPaymentAmount) {
      await postApplicationDownPayment(application.systemId, {
        downPaymentAmount,
        electronicPaymentSource: transformPaymentPlanRequest(request)
          .electronicPaymentSource,
      });
    }

    await postCloseoutRequest(application.systemId);

    const closeoutApp = await getApplication(application.systemId);

    if (!!closeoutApp.validationError) {
      const errors = closeoutApp.validationError.filter(
        (error) => error.typeCd !== 'Approval'
      );

      onFailure(errors.map((error) => error.msg).join(' '));
    } else {
      await postIssueRequest(application.systemId);
      onSuccess();
    }
  } catch (error) {
    if (!PRODUCTION) {
      console.error(error);
    }
    if ((error.response && (error.response.status === 503)) || (error.message == 'Network Error')) {
      navigateWithData('/server-maintenance', application.applicationNumber);
    } else {
      onFailure(error.message);
    }
  }
};

export const getPaymentPlans = async (applicationId) => {
  return await api.get(`/applications/${applicationId}/paymentPlans`);
};

export const getPaymentSources = async (applicationId) => {
  return await api.get(`/applications/${applicationId}/paymentSources`);
};
export const getAccountTypes = async () => {
  return await api.get(`/coderefs/UWPolicy/policy/pay-plan/account-type`);
};
export const getBankAccountTypes = async () => {
  return await api.get(`/coderefs/UWPolicy/policy/pay-plan/bank-account-type`);
};

export const getPaymentSupport = async (productVersionId) => {
  return await api.get(`/products/${productVersionId}/paymentSupport`);
};

export const postBindRequest = async (applicationId) =>
  api.post('/applications/' + applicationId + '/bindRequest');
export const updateApplicationPaymentPlans = async (applicationId, request) =>
  api.post(
    '/applications/' + applicationId + '/updatePaymentPlanRequest',
    request
  );
export const postApplicationDownPayment = (applicationId, request) =>
  api.post('/applications/' + applicationId + '/downPaymentRequest', request);
export const postCloseoutRequest = async (applicationId) =>
  api.post('/applications/' + applicationId + '/closeoutRequest');
export const postIssueRequest = async (applicationId) =>
  api.post('/applications/' + applicationId + '/issueRequest');

export const getBillingHistories = async (applicationId) =>
    api.get('/billingAccounts/' + applicationId + '/billingHistories');

const isZero = (payment) => {
  let result = true;
  // If the payment is not empty, null, or numeric 0, then check for text "0"
  if (payment) {
    if (typeof(payment) === "string") {
      result = (parseInt(payment) == 0);
    } else {
      result = false;  // Already checked for 0 above
    }
  }
  return result;
}

const transformPaymentPlanRequest = (paymentPlan) => {
  const { payPlanTypeCd, ...request } = paymentPlan;
  const { id, ...eps } = request.electronicPaymentSource;
  const {
    achName,
    achBankName,
    achBankAccountTypeCd,
    achBankAccountNumber,
    achRoutingNumber,
    achStandardEntryClassCd,
    ...creditCardSource
  } = eps;
  const {
    creditCardAuthorizationCd,
    creditCardAuthorizationMessage,
    creditCardExpirationMonth,
    creditCardExpirationYr,
    creditCardHolderName,
    creditCardNumber,
    creditCardSecurityCd,
    creditCardTypeCd,
    customerPaymentProfileId,
    customerProfileId,
    reminderDt,
    ...achSource
  } = eps;
  switch (request.paymentTypeCd) {
    case 'ACH': {
      return {
        ...request,
        electronicPaymentSource:
          payPlanTypeCd === 'Automatic'
            ? {...EMPTY_ELECTRONIC_PAYMENT_SOURCE, ...achSource}
            : undefined,
      };
    }
    case 'Credit Card': {
      return {
        ...request,
        electronicPaymentSource:
          payPlanTypeCd === 'Automatic'
            ? {
              ...EMPTY_ELECTRONIC_PAYMENT_SOURCE,
              ...creditCardSource,
              methodCd: 'Credit Card',
            }
            : undefined,
      };
    }
    default:
      return request;
  }
};

export const EMPTY_ELECTRONIC_PAYMENT_SOURCE = {
  achBankAccountNumber: '',
  achBankAccountTypeCd: '',
  achBankName: '',
  achName: '',
  achRoutingNumber: '',
  achStandardEntryClassCd: '',
  creditCardNumber: '',
  customerPaymentProfileId: '',
  customerProfileId: '',
  methodCd: '',
};

export async function paymentRequest(model, setState) {
  setState({
    loadingNext: true,
    inactive: true,
    paymentLoading: true,
  });

  const path = 'basicPolicy.electronicPaymentSource';
  const electronicPaymentSource = model.getValue(path);

  let updateRequest = {};

  if (electronicPaymentSource.existCardId === 'NewCard'){
    delete electronicPaymentSource.id;
  }
  delete electronicPaymentSource.existCardId;

  // if (electronicPaymentSource.achRoutingNumber ){
  //   delete electronicPaymentSource.achRoutingNumber;
  // }

  // The UI stores the credit-card method internally as 'CC', but the server
  // expects 'Credit Card'. Normalize it once here so EVERY payload built from
  // electronicPaymentSource below (updatePaymentPlanRequest + downPaymentRequest)
  // consistently carries 'Credit Card'. Only applied for the credit-card method.
  const isCreditCard = electronicPaymentSource.methodCd === 'CC';

  if (isCreditCard) {

    delete electronicPaymentSource.sourceTypeCd;

    electronicPaymentSource.methodCd = 'Credit Card';

    updateRequest = {
      electronicPaymentSource,
      payPlanCd: model.getValue('basicPolicy.payPlanCd'),
      paymentDay: model.getValue('basicPolicy.paymentDay'),
      paymentTypeCd: 'Credit Card',
    };

  } else if (electronicPaymentSource.methodCd === 'ACH') {
    const firstNamePath = 'insured.partyInfo[0].nameInfo.givenName';
    const firstName = model.getValue(firstNamePath);

    const lastNamePath = 'insured.partyInfo[0].nameInfo.surname';
    const lastName = model.getValue(lastNamePath);

    delete electronicPaymentSource.sourceTypeCd;

    electronicPaymentSource['achName'] = `${firstName} ${lastName}`;

    updateRequest = {
      electronicPaymentSource,
      payPlanCd: model.getValue('basicPolicy.payPlanCd'),
      paymentDay: model.getValue('basicPolicy.paymentDay'),
      paymentTypeCd: electronicPaymentSource.methodCd,
    };
  } else {
    updateRequest = {
      electronicPaymentSource,
      payPlanCd: model.getValue('basicPolicy.payPlanCd'),
      paymentDay: model.getValue('basicPolicy.paymentDay'),
      paymentTypeCd: electronicPaymentSource.methodCd,
      paymentId: electronicPaymentSource.paymentId,
    };
  }

  const downPayment = model.getValue('transactionInfo.paymentAmt');

  const postRequest = isCreditCard ?
    {
      downPaymentAmount: downPayment,
      electronicPaymentSource: {...electronicPaymentSource, methodCd: 'Credit Card'}
    } :
    {
      downPaymentAmount: downPayment,
      electronicPaymentSource
    }


  try {
    const result = await updateApplicationPaymentPlans(model.data.systemId, updateRequest);
    if (result === undefined) return (null);  // stop if not successful
    sendGoogleAnalyticsEvent({...GA_CONFIGURATION.Payment_Method, event_label: updateRequest.payPlanCd});

    if (!isZero(downPayment)) {
      await postApplicationDownPayment(model.data.systemId, postRequest);
    } else {
      console.log("No down-payment provided.  " + downPayment);
    }

    await postCloseoutRequest(model.data.systemId).then(async () => {
      const closeoutApp = await getApplication(model.data.systemId);

      let issues = [];

      traverseObject(closeoutApp, (obj, path) => {
        if (obj.issues) {
          obj.issues.forEach((issue) => {
            if (issue.msg) {
              issues.push(issue);
            }
          });
        }
      });

      if (issues.length > 0) {
        setState({
          loadingNext: false,
          inactive: false,
          paymentLoading: false,
        });
        return navigateWithData('/quote-summary', closeoutApp);
      } else {
        const policyInfo = await postIssueRequest(model.data.systemId)
            .then(async (res) => await api.get(res.headers.location))
            .then(async (res) => {
              return res.data;
            });

        let billingHistories = await getBillingHistories(policyInfo.accountRef)
            .then(res => res.data ? res.data.billingHistory : []).catch(e=>{
              console.log(e.response)
            });

        let transactionId = '';
        if(billingHistories && billingHistories.length > 0){
          billingHistories = billingHistories.filter(b=>b.receiptTypeCd === 'Credit Card' && b.typeCd === 'Receipt')
          billingHistories.sort((a,b)=>a.reference < b.reference);
          transactionId = billingHistories.length > 0 ? billingHistories[0].transactionId : '';
        }

        setState({
          loadingNext: false,
          inactive: false,
          paymentLoading: false,
        });

        return navigateWithData('/payment-successful', {
          ...model.data,
          customerRef: policyInfo.customerRef,
          policyNumber: policyInfo.basicPolicy.policyNumber,
          policyRef: policyInfo.systemId,
          transactionId: transactionId,
          policy: policyInfo,
        });
      }
    });
  } catch (error) {
    setState({
      loadingNext: false,
      inactive: false,
      paymentLoading: false,
      emailQuoteErrors: error.message,
    });
    await handleError(error, model.data.systemId, model.data.applicationNumber, 'paymentRequest');
  }
}

export const buyBindRequest = async (
  systemId,
  applicationNumber,
  navigate,
  navigateToStep,
  lastStep,
  setState,
  pullApplication
) => {
  try {
    //TODO: endpoint need to be changed later
    const obtainDataReport = (systemId) => {
      return api
          .post(`/applications/${systemId}/dataReportCheckAndApplyRequest`)
          .then((resDRC) => {
            if (resDRC.data.pendingDataReportExists === 'false' || resDRC.data.numberOfFailedDataReports > 0) {
              if (resDRC.data.numberOfFailedDataReports === 0) {
                api.get('/applications/' + systemId + '/full').then(TO_DATA).then(appUpdated => {
                  api.put('/applications/' + systemId + '/full', removeTempFields(appUpdated)).then(() => {
                    navigateToStep(lastStep);
                    pullApplication();  // run asynchronously
                  });
                });
              } else {
                onSubmitAgent(systemId,applicationNumber);
              }
              return resDRC.data;
            } else {
              // Try again
              return obtainDataReport(systemId);
            }
          });
    };
    api.post('/applications/' + systemId + '/bindRequest')
        .then(response => {
          obtainDataReport(systemId);
        })
        .catch(error => {
          console.log("Unable to bind application:", error);
          handleError(error, systemId, applicationNumber, 'buyBindRequest').then(() => {});
        });
  } catch (error) {
    await handleError(error, systemId, applicationNumber, 'buyBindRequest');
  } finally{
    enableNav(setState);
  }
};

// Method for data reports processed immediately
export const driverDataReportRequest = async (
    systemId,
    applicationNumber,
    navigate,
    navigateToStep,
    lastStep,
    emptyStep,
    setState,
    pullApplication,
    disabledSteps,
    setDisabledSteps
) => {
  const POLL_INTERVAL = 1000;
  const POLL_ATTEMPTS = 60;
  try {
    enableStep('vehiclesAndDrivers', disabledSteps, setDisabledSteps);
    const requestDataReportStatus = async() => {
      return api
        .get(`/applications/${systemId}/dataReportRequests/${dataReportId}/status`);
    }

    const validateResponse = res => res.status !== 202;

    let dataReportId = "";
    let completed = false;
    const prefillRequestResponse = await api.post(
      `/applications/${systemId}/dataReportPrefillRequest`
    );
    if (prefillRequestResponse.status === 200) {
      dataReportId = prefillRequestResponse.data.dataReportRequestId;

      const statusCheckResult = await pollRequest({
        fn: requestDataReportStatus,
        validate: validateResponse,
        interval: POLL_INTERVAL,
        maxAttempts: POLL_ATTEMPTS,
      });

      if (statusCheckResult.status && statusCheckResult.status === 200) {
        completed = statusCheckResult.data.requestStatusCd === "Applied";

        await pullApplication();
      } else {
        console.log("Data Report Status Failure Status: " + statusCheckResult.response.status + " Error Message: " + statusCheckResult.message);
      }

      if (completed) {
        navigateToStep(lastStep);
      } else {
        navigateToStep(emptyStep);
        setDisabledSteps([...disabledSteps,'vehiclesAndDrivers']);
      }
      enableNav(setState);
    }

  } catch (error) {
    await handleError(error, systemId, applicationNumber, 'driverDataReportRequest');
  }
};

// Method for requesting data reports processed on next save
export const driverDataReportStart = async (
    systemId,
    applicationNumber,
    navigate,
    navigateToStep,
    lastStep,
    emptyStep,
    setState,
    pullApplication,
    disabledSteps,
    setDisabledSteps
) => {
  const POLL_INTERVAL = 1000;
  const POLL_ATTEMPTS = 60;
  try {
    enableStep('vehiclesAndDrivers', disabledSteps, setDisabledSteps);
    const requestDataReportStatus = async() => {
      return api
          .get(`/applications/${systemId}/dataReportRequests/${dataReportId}/status?waitForDeferredStatus=Received`);
    }
    const validateResponse = res => res.status !== 202;

    let dataReportId = "";
    let issued = false;
    const prefillRequestResponse = await api.post(
        `/applications/${systemId}/dataReportPrefillRequest`
    );
    if (prefillRequestResponse.status === 200) {
      dataReportId = prefillRequestResponse.data.dataReportRequestId;
      const statusCheckResult = await pollRequest({
        fn: requestDataReportStatus,
        validate: validateResponse,
        interval: POLL_INTERVAL,
        maxAttempts: POLL_ATTEMPTS,
      });
      if (statusCheckResult.status && statusCheckResult.status === 200) {
        issued = statusCheckResult.data.reportStatusCd === "Received";
        await pullApplication();
      }
    } else {
      console.log("Data Report Start failure: " + prefillRequestResponse.status + " Error Message: " + prefillRequestResponse.message);
    }

    if (issued) {
      navigateToStep(lastStep);
    } else {
      navigateToStep(emptyStep);
      setDisabledSteps([...disabledSteps,'vehiclesAndDrivers']);
    }
    enableNav(setState);
    return dataReportId;  // This number must be squirrled away to retrieve report status later
  } catch (error) {
    await handleError(error, systemId, applicationNumber, 'driverDataReportStart');
  }
};

// Method for waiting for data reports to be applied
export const driverDataReportComplete = async (
    systemId,
    dataReportId,
    applicationNumber,
    navigate,
    navigateToStep,
    lastStep,
    emptyStep,
    setState,
    pullApplication,
    disabledSteps,
    setDisabledSteps
) => {
  const POLL_INTERVAL = 1000;
  const POLL_ATTEMPTS = 60;
  try {
    enableStep('vehiclesAndDrivers', disabledSteps, setDisabledSteps);
    const index = disabledSteps.indexOf('vehiclesAndDrivers')

    const requestDataReportStatus = async() => {
      return api
          .get(`/applications/${systemId}/dataReportRequests/${dataReportId}/status?waitForDeferredStatus=Applied`);
    }
    const validateResponse = res => res.status !== 202;

    let completed = false;

    const statusCheckResult = await pollRequest({
      fn: requestDataReportStatus,
      validate: validateResponse,
      interval: POLL_INTERVAL,
      maxAttempts: POLL_ATTEMPTS,
    });

    if (statusCheckResult.status && statusCheckResult.status === 200) {
      completed = statusCheckResult.data.requestStatusCd === "Applied";
      await pullApplication();
    } else {
      console.log("Data Report Status Failure Status: " + statusCheckResult.response.status + " Error Message: " + statusCheckResult.message);
    }

    if (completed) {
      navigateToStep(lastStep);
    } else {
      navigateToStep(emptyStep);
      setDisabledSteps([...disabledSteps,'vehiclesAndDrivers']);
    }
    enableNav(setState);

  } catch (error) {
    await handleError(error, systemId, applicationNumber, 'driverDataReportComplete');
  }
};

const enableStep = (stepName, disabledSteps, setDisabledSteps) => {
  const index = disabledSteps.indexOf('vehiclesAndDrivers')
  if (index !== -1){
    const newDisabledSteps = [...disabledSteps];
    newDisabledSteps.splice(index,1);
    setDisabledSteps(newDisabledSteps);
  }
}

const enableNav = (setState) => {
  setState({
    loadingBack: false,
    loadingNext: false,
  });
  scrollTo(0, 0);
}

const pollRequest = async ({ fn, validate, interval, maxAttempts }) => {
  let attempts = 0;

  const executePoll = async (resolve, reject) => {
    try {
      const result = await fn();
      attempts++;

      if (validate(result)) {
        return resolve(result);
      } else if (maxAttempts && attempts === maxAttempts) {
        return reject(new Error('Exceeded max attempts'));
      } else {
        setTimeout(executePoll, interval, resolve, reject);
      }
    } catch (error) {
      return resolve(error);
    }
  };

  return new Promise(executePoll);
};